import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles, X, Zap } from 'lucide-react';

interface ComingSoonDialogProps {
  open: boolean;
  onClose: () => void;
}

const ComingSoonDialog = ({ open, onClose }: ComingSoonDialogProps) => {
  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl border border-border-primary bg-background-card shadow-2xl"
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 16 }}
            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Top gradient banner */}
            <div className="relative flex flex-col items-center gap-4 bg-gradient-to-br from-[#1a5c2a] via-[#2f7d32] to-[#16304c] px-8 py-10 text-white">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_60%_30%,rgba(255,255,255,0.07),transparent_60%)]" />

              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.05 }}
                className="flex h-20 w-20 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-sm"
              >
                <Zap className="h-9 w-9" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12 }}
                className="text-center"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">
                  Coming Soon
                </p>
                <h2 className="mt-1 text-2xl font-bold">We&apos;re Building This</h2>
                <p className="mt-2 text-sm leading-6 text-white/80">
                  This feature will be available very soon. Our team is working hard to get it ready for you.
                </p>
              </motion.div>
            </div>

            {/* Bottom content */}
            <div className="space-y-4 p-6">
              <div className="flex items-start gap-3 rounded-2xl border border-border-primary/60 bg-background-secondary p-4">
                <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-accent-primary" />
                <p className="text-sm leading-6 text-text-secondary">
                  In the meantime, feel free to call us directly. Our team is ready to help you plan your solar project today.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a
                  href="tel:8433135253"
                  className="flex items-center justify-center gap-2 rounded-xl border border-border-primary/60 bg-background-secondary px-4 py-3 text-sm font-medium text-text-primary transition-colors hover:border-accent-primary hover:text-accent-primary"
                >
                  📞 8433135253
                </a>
                <a
                  href="tel:9720093262"
                  className="flex items-center justify-center gap-2 rounded-xl border border-border-primary/60 bg-background-secondary px-4 py-3 text-sm font-medium text-text-primary transition-colors hover:border-accent-primary hover:text-accent-primary"
                >
                  📞 9720093262
                </a>
              </div>

              <button
                onClick={onClose}
                className="w-full rounded-xl bg-accent-primary px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-primary/90"
              >
                Got it, thanks!
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ComingSoonDialog;
